/**
 * @fileoverview Componente de visualização da landpage configurada.
 * Recebe um JSON gerado pelo LandpageManager e renderiza as seções
 * na sequência definida, respeitando o modo de exibição de cada uma.
 *
 * Modos suportados:
 * - carousel: slides com navegação lateral (setas + indicadores)
 * - grid: grade responsiva de cards
 * - sequential: itens empilhados verticalmente
 *
 * @module LandpageView
 * @since 1.0.0
 */

import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardMedia,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { apiRequestJson } from '../../api/apiService';
import { selectPromotions } from '../../store/slices/globalDataSlice';
import ProductModal from '../Product/ProductModal';

// ---------------------------------------------------------------------------
// Card de produto para landpage (visual próprio + ProductModal ao clicar)
// ---------------------------------------------------------------------------

/**
 * Card de produto com visual otimizado para landpage.
 * Abre ProductModal ao clicar, mantendo o mesmo comportamento do ProductCard.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.product - Dados do produto
 * @param {boolean} [props.fixedHeight=false] - Se deve ocupar altura total do container
 * @returns {JSX.Element}
 */
function LandpageProductCard({ product, fixedHeight = false }) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Card
        onClick={() => setModalOpen(true)}
        sx={{
          width: '100%',
          height: fixedHeight ? '100%' : 320,
          display: 'flex',
          borderRadius: 2,
          boxShadow: 2,
          cursor: 'pointer',
          overflow: 'hidden',
          '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s' },
        }}
      >
        <CardMedia
          component="img"
          image={product.imagem || '/vika_icon_light.gif'}
          alt={product.nome}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: product.imagem ? 'cover' : 'contain',
            objectPosition: 'center',
          }}
        />
      </Card>
      <ProductModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        product={product}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: carrossel de itens
// ---------------------------------------------------------------------------

/**
 * Renderiza itens em modo carrossel com scroll nativo suave.
 * - Ocupa 100% da largura da página (desktop e mobile)
 * - Suporte a touch/swipe nativo via CSS scroll-snap
 * - Rolagem suave controlada pelos botões prev/next via scrollBy
 * - Indicadores de posição sincronizados com o scroll
 *
 * @component
 * @param {Object} props
 * @param {Array} props.items - Itens a exibir
 * @returns {JSX.Element}
 */
function CarouselSection({ items }) {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const cardWidth = isMobile ? 200 : 260;
  const cardGap = 16;

  /**
   * Avança ou recua o carrossel rolando pelo tamanho de um card + gap.
   * @param {'prev'|'next'} direction
   */
  const handleScroll = (direction) => {
    if (!scrollRef.current) return;
    const delta = (cardWidth + cardGap) * (isMobile ? 1 : 2);
    scrollRef.current.scrollBy({ left: direction === 'next' ? delta : -delta, behavior: 'smooth' });
  };

  /**
   * Atualiza o indicador ativo conforme o scroll.
   */
  const handleScrollEvent = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const maxScroll = scrollWidth - clientWidth;
    // map: calcula índice proporcional à posição do scroll
    const index = maxScroll > 0 ? Math.round((scrollLeft / maxScroll) * (items.length - 1)) : 0;
    setActiveIndex(index);
  };

  /**
   * Rola para o item correspondente ao indicador clicado.
   * @param {number} i - Índice do indicador
   */
  const handleDotClick = (i) => {
    if (!scrollRef.current) return;
    const { scrollWidth, clientWidth } = scrollRef.current;
    const maxScroll = scrollWidth - clientWidth;
    scrollRef.current.scrollTo({ left: (i / (items.length - 1)) * maxScroll, behavior: 'smooth' });
  };

  return (
    <Box sx={{ position: 'relative', width: '100vw', left: '50%', transform: 'translateX(-50%)' }}>
      {/* Botão prev */}
      <IconButton
        onClick={() => handleScroll('prev')}
        sx={{
          position: 'absolute', left: 8, top: '50%', transform: 'translateY(-60%)',
          zIndex: 2, bgcolor: 'background.paper', boxShadow: 2,
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        <ArrowBackIosIcon fontSize="small" />
      </IconButton>

      {/* Trilha de scroll nativo com snap */}
      <Box
        ref={scrollRef}
        onScroll={handleScrollEvent}
        sx={{
          display: 'flex',
          gap: `${cardGap}px`,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          px: { xs: 2, sm: 6 },
          py: 1,
          // esconde scrollbar visualmente mas mantém funcional
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {/* map: renderiza todos os cards com snap individual */}
        {items.map((product) => (
          <Box
            key={product.id}
            sx={{
              flex: `0 0 ${cardWidth}px`,
              scrollSnapAlign: 'start',
              height: 320,
            }}
          >
            <LandpageProductCard product={product} fixedHeight />
          </Box>
        ))}
      </Box>

      {/* Botão next */}
      <IconButton
        onClick={() => handleScroll('next')}
        sx={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-60%)',
          zIndex: 2, bgcolor: 'background.paper', boxShadow: 2,
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        <ArrowForwardIosIcon fontSize="small" />
      </IconButton>

      {/* Indicadores de posição */}
      {items.length > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
          {/* map: renderiza ponto indicador para cada item */}
          {items.map((_, i) => (
            <Box
              key={i}
              onClick={() => handleDotClick(i)}
              sx={{
                width: i === activeIndex ? 16 : 8,
                height: 8,
                borderRadius: '4px',
                bgcolor: i === activeIndex ? 'primary.main' : 'grey.400',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: grid responsivo de itens
// ---------------------------------------------------------------------------

/**
 * Renderiza itens em grade responsiva.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.items - Itens a exibir
 * @returns {JSX.Element}
 */
function GridSection({ items }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
        gap: { xs: 1, sm: 2, md: 3 },
      }}
    >
      {/* map: renderiza cada produto em célula do grid responsivo */}
      {items.map((product) => (
        <LandpageProductCard key={product.id} product={product} />
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: lista sequencial de itens
// ---------------------------------------------------------------------------

/**
 * Renderiza itens empilhados verticalmente.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.items - Itens a exibir
 * @returns {JSX.Element}
 */
function SequentialSection({ items }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* map: renderiza cada produto empilhado verticalmente */}
      {items.map((product) => (
        <LandpageProductCard key={product.id} product={product} />
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: seção de promoção
// ---------------------------------------------------------------------------

/**
 * Renderiza uma seção completa de promoção com o modo de exibição definido.
 * Resolve todos os targets (produtos, categorias, marcas) em produtos com imagem
 * e os exibe conforme o displayMode configurado.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.section - Configuração da seção (promotionId, promotionName, displayMode)
 * @returns {JSX.Element}
 */
function PromotionSection({ section }) {
  const allPromotions = useSelector(selectPromotions);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const promotion = allPromotions.find(
    (p) => (p.id || p._id) === section.promotionId
  );

  useEffect(() => {
    const loadProducts = async () => {
      if (!promotion) { setLoading(false); return; }
      const targets = promotion.targets || {};
      const collected = [];

      /**
       * Normaliza um array de targets para array de IDs strings,
       * independente de ser [{id, name}] ou ["id1", "id2"].
       * @param {Array} arr
       * @returns {Array<string>}
       */
      const toIds = (arr = []) =>
        arr.map((item) => (typeof item === 'object' ? String(item.id || item._id) : String(item)));

      const productIds = toIds(targets.products);
      const categoryIds = toIds(targets.categories);
      const brandIds = toIds(targets.brands);

      try {
        const allProductsRes = await apiRequestJson('products_list');
        const allProducts = (allProductsRes.success && allProductsRes.data) ? allProductsRes.data : [];

        // filter: mantém apenas produtos ativos e não removidos
        const activeProducts = allProducts.filter(
          (p) => p.ativo !== false && p.ativo !== 0 && p.removido !== true && p.removido !== 1
        );

        if (productIds.length > 0) {
          // filter: mantém apenas produtos que estão nos targets da promoção
          const direct = activeProducts.filter((p) => productIds.includes(String(p.id)));
          collected.push(...direct);
        }

        if (categoryIds.length > 0) {
          const catRes = await apiRequestJson('products_categories');
          if (catRes.success && catRes.data) {
            // filter: categorias que estão nos targets
            const targetCats = catRes.data.filter((c) => categoryIds.includes(String(c.id)));
            // flatMap: expande cada categoria nos seus produtos ativos
            const catProducts = targetCats.flatMap((cat) =>
              activeProducts.filter((p) => p.categoria?.toLowerCase() === cat.name?.toLowerCase())
            );
            collected.push(...catProducts);
          }
        }

        if (brandIds.length > 0) {
          const brandRes = await apiRequestJson('products_brands');
          if (brandRes.success && brandRes.data) {
            // filter: marcas que estão nos targets
            const targetBrands = brandRes.data.filter((b) => brandIds.includes(String(b.id)));
            // flatMap: expande cada marca nos seus produtos ativos
            const brandProducts = targetBrands.flatMap((brand) =>
              activeProducts.filter((p) => p.marca?.toLowerCase() === brand.name?.toLowerCase())
            );
            collected.push(...brandProducts);
          }
        }

        const seen = new Set();
        // filter: remove produtos duplicados mantendo a primeira ocorrência
        const unique = collected.filter((p) => {
          if (seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });

        setProducts(unique);
      } catch (err) {
        console.error('Erro ao carregar produtos da seção:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [promotion]);

  const renderWithMode = (itemList) => {
    if (itemList.length === 0) return null;
    if (section.displayMode === 'carousel') return <CarouselSection items={itemList} />;
    if (section.displayMode === 'grid') return <GridSection items={itemList} />;
    return <SequentialSection items={itemList} />;
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        {section.promotionName}
      </Typography>

      {promotion?.context?.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {promotion.context.description}
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {!loading && products.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nenhum produto encontrado nesta promoção.
        </Typography>
      )}

      {!loading && products.length > 0 && renderWithMode(products)}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

/**
 * Visualizador da landpage configurada.
 * Recebe um JSON gerado pelo LandpageManager e renderiza as seções em sequência.
 *
 * Pode ser usado de duas formas:
 * 1. Embutido no Workspace (sem modal) — passar apenas `config`
 * 2. Em modal de preview — passar `open`, `onClose` e `config`
 *
 * @component
 * @param {Object} props
 * @param {Object} props.config - JSON de configuração gerado pelo LandpageManager
 * @param {boolean} [props.open] - Controla visibilidade do modal (opcional)
 * @param {Function} [props.onClose] - Callback para fechar modal (opcional)
 * @returns {JSX.Element}
 */
export default function LandpageView({ config, open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sections = config?.sections || [];

  const content = (
    <Container maxWidth="lg" sx={{ py: 3, px: { xs: 1, sm: 3 }, overflowX: 'visible' }}>
      {sections.length === 0 && (
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
          Nenhuma seção configurada.
        </Typography>
      )}

      {/* map: renderiza cada seção da landpage na ordem definida */}
      {sections.map((section, index) => (
        <Box key={section.id || index}>
          <PromotionSection section={section} />
          {index < sections.length - 1 && <Divider sx={{ mb: 4 }} />}
        </Box>
      ))}
    </Container>
  );

  // Modo modal (preview)
  if (open !== undefined) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Preview da Landpage
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  // Modo embutido no Workspace
  return (
    <Box component="main" 
        sx={{ pt: 8, pb: 6, minHeight: '100vh' }}>
        <Container
          maxWidth='lg'
          sx={{
            py: 4,
            mt: { xs: 3, sm: 4, md: '100px' },
            px: { xs: 3, sm: 4, md: 3 },
          }}
        >
          {content}
        </Container>
    </Box>
  );
}
